// Client-side /expansion/team/{code} play-style page. Reads data-team-* off the
// shell root, fetches the prebuilt profile JSON
// (/expansion-data/profiles/{CODE}.json — the expansion_profiles asset output,
// incl. a prebuilt chart_svg), and renders the per-game metric table, current
// roster, and lost-to-expansion sections — mirroring templates/expansion_team.html.
// Pure rendering of a render-ready JSON; no browser DB.

function esc(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function gloss(slug: string): string {
  // Node-safe (the parity/smoke harness has no `window`): the glossary global is
  // injected by base.html in the browser.
  const w = (typeof window !== "undefined" ? window : undefined) as unknown as
    { __PWHL_GLOSS__?: Record<string, string> } | undefined;
  return (w?.__PWHL_GLOSS__?.[slug]) || "";
}
function titleAttr(slug: string): string {
  const g = gloss(slug);
  return g ? ` title="${esc(g)}"` : "";
}

// ---- value formatters (mirror the Jinja macros in expansion_team.html) ----
type Num = number | null | undefined;
const isNil = (x: Num): x is null | undefined => x === null || x === undefined;
const v = (x: Num): string => (isNil(x) ? "—" : String(x));
const pg = (x: Num): string => (isNil(x) ? "—" : (x as number).toFixed(2));
const delta = (x: Num): string =>
  isNil(x) ? "—" : `${(x as number) >= 0 ? "+" : ""}${(x as number).toFixed(2)}`;
const svp = (x: Num): string => (isNil(x) ? "—" : (x as number).toFixed(3).replace(/^0/, ""));
const gaa = (x: Num): string => (isNil(x) ? "—" : (x as number).toFixed(2));
const pct = (x: Num): string => (isNil(x) ? "—" : `${(x as number).toFixed(0)}%`);
const xg = (x: Num): string => (isNil(x) ? "—" : (x as number).toFixed(1));

// ---- profile JSON shape (subset we render) ----
interface SkaterRow {
  name: string; position: string; league: string; box_only: boolean; drafted: boolean;
  gp: Num; g: Num; a1: Num; a2: Num; points: Num; hits: Num; blocks: Num; pim: Num; plus_minus: Num;
}
interface GoalieRow {
  name: string; league?: string; box_only?: boolean; drafted?: boolean;
  gp: Num; w: Num; l: Num; sv_pct: Num; gaa: Num; so?: Num;
}
interface Shares { goals: Num; points: Num; shots: Num; blocks: Num; hits: Num }
interface LostPlayer {
  name: string; position: string; goals: Num; points: Num; shots: Num; blocks: Num; hits: Num;
  shares: Shares; xgf: Num; xga: Num; xgf_pct: Num;
}
interface Profile {
  kind: string;
  metric_meta: Array<{ key: string; label: string; slug: string }>;
  after: { metrics: Record<string, Num>; n_with_stats: number; n_skaters: number; total_gp: number };
  league_avg: Record<string, Num>;
  delta?: Record<string, Num>;
  roster: { forwards: SkaterRow[]; defense: SkaterRow[]; goalies: GoalieRow[] };
  lost_impact?: {
    players: LostPlayer[]; goalies: GoalieRow[]; n: number;
    aggregate: { goals: Num; points: Num; shots: Num; blocks: Num; hits: Num; shares: Shares };
  };
  lost_gained?: { chart_svg?: string; n_drafted: number; n_signed: number };
}

// ---- table renderers (mirror skater_table / goalie_table / roster_block) ----
function skaterTable(rows: SkaterRow[]): string {
  const body = rows.map((r) => `<tr${r.box_only ? ' class="exp-row-box"' : ""}>
      <th>${esc(r.name)} <span class="exp-row-pos">${esc(r.position)}</span>${r.drafted ? ' <span class="exp-row-drafted" title="2026 Entry Draft pick">Drafted</span>' : ""}</th>
      <td class="exp-league">${esc(r.league)}</td>
      <td>${v(r.gp)}</td><td>${v(r.g)}</td><td>${v(r.a1)}</td><td>${v(r.a2)}</td>
      <td>${v(r.points)}</td><td>${v(r.hits)}</td><td>${v(r.blocks)}</td>
      <td>${v(r.pim)}</td><td>${v(r.plus_minus)}</td>
    </tr>`).join("");
  return `<div class="table-scroll"><table class="results exp-roster-table">
      <thead><tr>
        <th>Player</th><th>League</th><th>GP</th><th>G</th>
        <th${titleAttr("a")}>A1</th><th${titleAttr("a2")}>A2</th>
        <th>PTS</th><th${titleAttr("hit")}>Hits</th>
        <th${titleAttr("blk")}>Blk</th><th>PIM</th>
        <th${titleAttr("plus_minus")}>+/-</th>
      </tr></thead>
      <tbody>${body}</tbody>
    </table></div>`;
}
function goalieTable(rows: GoalieRow[], withSo: boolean): string {
  const body = rows.map((r) => `<tr${r.box_only ? ' class="exp-row-box"' : ""}>
      <th>${esc(r.name)}${r.drafted ? ' <span class="exp-row-drafted" title="2026 Entry Draft pick">Drafted</span>' : ""}</th>
      <td class="exp-league">${esc(r.league ?? "")}</td>
      <td>${v(r.gp)}</td><td>${v(r.w)}</td><td>${v(r.l)}</td>
      <td>${svp(r.sv_pct)}</td><td>${gaa(r.gaa)}</td>${withSo ? `<td>${v(r.so)}</td>` : ""}
    </tr>`).join("");
  return `<div class="table-scroll"><table class="results exp-roster-table">
      <thead><tr>
        <th>Goalie</th><th>League</th><th>GP</th><th>W</th><th>L</th>
        <th${titleAttr("sv_pct")}>SV%</th><th${titleAttr("gaa")}>GAA</th>${withSo ? "<th>SO</th>" : ""}
      </tr></thead>
      <tbody>${body}</tbody>
    </table></div>`;
}
function rosterBlock(roster: Profile["roster"]): string {
  let out = "";
  if (roster.forwards.length) out += `<h4 class="exp-subhead">Forwards</h4>${skaterTable(roster.forwards)}`;
  if (roster.defense.length) out += `<h4 class="exp-subhead">Defense</h4>${skaterTable(roster.defense)}`;
  if (roster.goalies.length) out += `<h4 class="exp-subhead">Goaltenders</h4>${goalieTable(roster.goalies, true)}`;
  return out;
}

// ---- section renderers ----
function metricSection(p: Profile, kind: string): string {
  const existing = kind === "existing";
  const sub = `Each stat is the rostered skaters' combined output — summed across players, divided by their
    total games played — i.e. the average of the players' individual per-game production, not the
    team's per-game total. Shown vs the league average${existing ? ", with the change from the previous (2025-26) roster" : ""}.`;
  const rows = p.metric_meta.map((m) => {
    const d = p.delta?.[m.key];
    const cls = existing ? (d && (d as number) > 0 ? "exp-up" : d && (d as number) < 0 ? "exp-down" : "") : "";
    return `<tr>
      <th${titleAttr(m.slug)}>${esc(m.label)}</th>
      <td>${pg(p.after.metrics[m.key])}</td>
      <td class="exp-muted">${pg(p.league_avg[m.key])}</td>
      ${existing ? `<td class="${cls}">${delta(d)}</td>` : ""}
    </tr>`;
  }).join("");
  return `<section class="exp-metric-section">
      <h3 class="exp-section-title">Player per-game averages</h3>
      <p class="exp-section-sub">${sub}</p>
      <div class="table-scroll"><table class="results exp-metric-table">
        <thead><tr><th>Per game</th><th>This roster</th><th>League avg</th>${existing ? "<th>vs 2025-26</th>" : ""}</tr></thead>
        <tbody>${rows}</tbody>
      </table></div>
    </section>`;
}

function coverageBanner(p: Profile): string {
  const a = p.after;
  return `<section class="exp-coverage-banner">
      <p>Each rate combines <strong>${a.n_with_stats}</strong> of ${a.n_skaters}
        skaters' box scores across ${a.total_gp} player-games: total output ÷ total games
        played — a games-played–weighted average of the players' individual per-game rates, not the
        team's per-game total.</p>
      <p class="exp-coverage-caveat">Rates are per game played and aren't competition-adjusted across leagues — an NCAA goal-per-game
        isn't a PWHL goal-per-game. The league column on each player is the context for reading them.</p>
    </section>`;
}

function rosterSection(p: Profile): string {
  const r = p.roster;
  const body = r.forwards.length || r.defense.length || r.goalies.length
    ? rosterBlock(r) : '<p class="exp-empty">No signings yet.</p>';
  return `<section class="exp-roster-section">
      <h3 class="exp-section-title">Current roster</h3>${body}</section>`;
}

function lostImpactSection(p: Profile, name: string): string {
  const li = p.lost_impact;
  if (p.kind !== "existing" || !li || (!li.players.length && !li.goalies.length)) return "";
  const agg = li.aggregate;

  const headline = li.players.length
    ? `<p class="exp-impact-headline">${esc(name)}'s ${li.n} departed skater${li.n !== 1 ? "s" : ""} accounted for
        <strong>${pct(agg.shares.goals)}</strong> of team goals,
        <strong>${pct(agg.shares.points)}</strong> of points,
        <strong>${pct(agg.shares.shots)}</strong> of shots,
        <strong>${pct(agg.shares.blocks)}</strong> of blocks, and
        <strong>${pct(agg.shares.hits)}</strong> of hits.</p>`
    : "";

  const lg = p.lost_gained;
  const chart = lg?.chart_svg
    ? `<h4 class="exp-subhead">Who left vs who's arriving — per game</h4>
      <p class="exp-section-sub">The departed cohort's 2025-26 PWHL production against the incoming class
        (${lg.n_drafted} draft pick${lg.n_drafted !== 1 ? "s" : ""}${lg.n_signed ? `, ${lg.n_signed} signing${lg.n_signed !== 1 ? "s" : ""}/trade` : ""}), as
        per-game rates. Incoming rates are each player's established pace in their own league —
        PWHL arrivals are directly comparable; college/European draftees post a higher pace
        against weaker competition, so their side is <strong>not competition-adjusted</strong>
        (read it as a ceiling, not a forecast).</p>
      <figure class="exp-chart">
        <div class="exp-impact-chart">${lg.chart_svg}</div>
        <figcaption class="exp-chart-legend">
          <span class="exp-legend-swatch exp-legend-out"></span> left the team
          <span class="exp-legend-swatch exp-legend-in"></span> arriving (draft / signings)
        </figcaption>
      </figure>`
    : "";

  const playerTable = li.players.length
    ? `<div class="table-scroll"><table class="results exp-roster-table exp-impact-table">
        <thead><tr>
          <th>Player</th>
          <th${titleAttr("g")}>G</th><th${titleAttr("pts")}>PTS</th><th${titleAttr("sog")}>Shots</th>
          <th${titleAttr("blk")}>Blk</th><th${titleAttr("hit")}>Hits</th>
          <th${titleAttr("xgf")}>xGF</th><th${titleAttr("xga")}>xGA</th><th${titleAttr("xgf_pct")}>xGF%</th>
        </tr></thead>
        <tbody>
          ${li.players.map((r) => `<tr>
            <th>${esc(r.name)} <span class="exp-row-pos">${esc(r.position)}</span></th>
            <td>${v(r.goals)} <span class="exp-share">${pct(r.shares.goals)}</span></td>
            <td>${v(r.points)} <span class="exp-share">${pct(r.shares.points)}</span></td>
            <td>${v(r.shots)} <span class="exp-share">${pct(r.shares.shots)}</span></td>
            <td>${v(r.blocks)} <span class="exp-share">${pct(r.shares.blocks)}</span></td>
            <td>${v(r.hits)} <span class="exp-share">${pct(r.shares.hits)}</span></td>
            <td>${xg(r.xgf)}</td><td>${xg(r.xga)}</td><td>${pct(r.xgf_pct)}</td>
          </tr>`).join("")}
          <tr class="exp-impact-total">
            <th>Departed total</th>
            <td>${v(agg.goals)} <span class="exp-share">${pct(agg.shares.goals)}</span></td>
            <td>${v(agg.points)} <span class="exp-share">${pct(agg.shares.points)}</span></td>
            <td>${v(agg.shots)} <span class="exp-share">${pct(agg.shares.shots)}</span></td>
            <td>${v(agg.blocks)} <span class="exp-share">${pct(agg.shares.blocks)}</span></td>
            <td>${v(agg.hits)} <span class="exp-share">${pct(agg.shares.hits)}</span></td>
            <td>—</td><td>—</td><td>—</td>
          </tr>
        </tbody>
      </table></div>`
    : "";

  const goaliesLost = li.goalies.length
    ? `<h4 class="exp-subhead">Goaltenders lost</h4>
      <div class="table-scroll"><table class="results exp-roster-table">
        <thead><tr><th>Goalie</th><th>GP</th><th>W</th><th>L</th>
          <th${titleAttr("sv_pct")}>SV%</th><th${titleAttr("gaa")}>GAA</th></tr></thead>
        <tbody>${li.goalies.map((r) => `<tr>
          <th>${esc(r.name)}</th>
          <td>${v(r.gp)}</td><td>${v(r.w)}</td><td>${v(r.l)}</td>
          <td>${svp(r.sv_pct)}</td><td>${gaa(r.gaa)}</td>
        </tr>`).join("")}</tbody>
      </table></div>`
    : "";

  return `<section class="exp-lost-impact-section">
      <h3 class="exp-section-title">Lost to expansion</h3>
      <p class="exp-section-sub">How much of ${esc(name)}'s 2025-26 regular-season output left with the players
        poached in expansion — the hole the team has to fill. Shares are each player's (or
        the group's) total as a percentage of the team total. On-ice xG (expected goals for
        / against while the player was on the ice) is two-way context, not a team share.</p>
      ${headline}${chart}${playerTable}${goaliesLost}
    </section>`;
}

export function renderProfile(p: Profile, name: string): string {
  return coverageBanner(p) + metricSection(p, p.kind) + rosterSection(p) + lostImpactSection(p, name);
}

export function initExpansionTeam(): boolean {
  const root = document.getElementById("exp-team-root");
  if (!root) return false;
  const code = root.dataset["teamCode"] ?? "";
  const name = root.dataset["teamName"] ?? "";
  if (!code) return false;

  root.innerHTML = '<p class="empty" style="padding:1.5rem;text-align:center;">Loading…</p>';
  void (async () => {
    try {
      const resp = await fetch(`/expansion-data/profiles/${code.toUpperCase()}.json`);
      if (!resp.ok) {
        root.innerHTML = `<section class="exp-empty-block"><p class="exp-empty">The report for this team hasn't been built yet. Run
          <code>python -m scripts.build_expansion_profiles --force</code> after ingest.</p></section>`;
        return;
      }
      const profile = (await resp.json()) as Profile;
      root.innerHTML = renderProfile(profile, name);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      root.innerHTML = `<p class="empty">Couldn’t load the team report: ${esc(msg)}</p>`;
      console.error("[expansion-team] load failed:", err);
    }
  })();
  return true;
}
